import React, { useState, useEffect } from 'react';
import { getJwtToken } from "../auth";
import { toggleTwoFactorAuth, fetchProfile, fetchJobTypes, fetchJobSummaries } from '../apiServices';
import config from "../config";
import Button from "../components/button";

import { useNotifications } from '../notifications/NotificationContext';

const reactServer = config.reactServer;


interface PreferencesModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type SummaryType = number[];
type SummaryTypeData = {
    id: number;
    name: string;
};
type JobType = {
    id: number;
    job_name: string;
};

type JobSummary = {
    job_id: number;
    summarie_id: number;
}

type JobSummariesResponse = {
    job_summaries: JobSummary[];
    message: string;
}

const updateFirstLoginStatus = async () => {
    const token = getJwtToken();
    if (!token) {
        console.error('No token found');
        return;
    }

    try {
        const response = await fetch(`${config.reactServer}feedback/update-first-login`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ firstLogin: false })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        // console.log('First login status updated:', data);
    } catch (error) {
        console.error('Error updating first login status:', error);
    }
};

export const PreferencesModal: React.FC<PreferencesModalProps> = ({ isOpen, onClose }) => {
    const { addNotification } = useNotifications();
    const [currentStep, setCurrentStep] = useState(1);
    const [profile, setProfile] = useState<any>(null);

    useEffect(() => {
        const fetchAndSetProfile = async () => {
            try {
                const data = await fetchProfile();
                setProfile(data);
            } catch (error) {
                console.error("Error fetching profile:", error);
            }
        };
    
        fetchAndSetProfile();
    }, []);

    const [preferences, setPreferences] = useState({
        profession: null as number | null,
        summaryType: [] as number[],
    });

    const [selectedProfession, setselectedProfession] = useState<number | null>(null);
    const [selectedSummaryTypes, setSelectedSummaryTypes] = useState<number[]>([]);
    const [availableJobs, setAvailableJobs] = useState<JobType[]>([]);
    const [availableSummaries, setAvailableSummaries] = useState<SummaryTypeData[]>([]);

    const handleProfessionSelect = (profession: number) => {
        const professionChanged = profession !== selectedProfession;
        
        if (professionChanged) {
            setselectedProfession(profession);
            setSelectedSummaryTypes([]); 
            setPreferences(prev => ({
                ...prev,
                profession: profession,
                summaryType: [] 
            }));
        } else {
            setselectedProfession(profession);
            setPreferences(prev => ({
                ...prev,
                profession: profession,
                summaryType: selectedSummaryTypes 
            }));
        }
        
        setCurrentStep(2);
    };

    const handleSummaryTypeSelect = (summaryTypes: number[]) => {
        setSelectedSummaryTypes(summaryTypes);
        setPreferences({ ...preferences, summaryType: summaryTypes });
        setCurrentStep(3);
    };

    const handleSettingsComplete = async () => {
        const token = getJwtToken();
        if (!token) {
            console.error('No token found');
            return;
        }

        const lowestSummaryId = Math.min(...selectedSummaryTypes);

        const preferenceData = {
            professions: selectedProfession,
            summaryTypes: selectedSummaryTypes,
            defaultSummaryType: lowestSummaryId
        };

        try {
            const response = await fetch(`${config.reactServer}feedback/preferences-save`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(preferenceData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            // console.log('Preferences saved successfully:', data);
            
            await updateFirstLoginStatus();
            onClose(); 
        } catch (error) {
            console.error('Error saving preferences:', error);
        }
    };

    const handleSkip = () => {
        // console.log('Setup overgeslagen, gebruik default waardes');
    };

    const handleClose = async () => {
        await updateFirstLoginStatus();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="flex flex-col items-center fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 mt-[80px] md:mt-[100px] p-4 md:p-4">
            <div className="relative bg-white rounded-lg max-h-[90vh] overflow-y-auto">
                <button 
                    onClick={handleClose}
                    className="absolute top-0 right-2 text-gray-500 hover:text-gray-700"
                >
                    ×
                </button>
                
                <div>
                    {currentStep === 1 && (
                        <ProfessionStep 
                            onNext={handleProfessionSelect} 
                            initialSelected={selectedProfession}
                            setAvailableJobs={setAvailableJobs}
                            availableJobs={availableJobs}
                            profile={profile}
                        />
                    )}
                    {currentStep === 2 && (
                        <SummaryTypeStep
                            onNext={(summaryTypes) => {
                                handleSummaryTypeSelect(summaryTypes);
                            }}
                            onBack={() => setCurrentStep(1)}
                            selectedProfession={selectedProfession}
                            initialSelected={selectedSummaryTypes}
                            setAvailableSummaries={setAvailableSummaries}
                        />
                    )}
                    {currentStep === 3 && (
                        <OtherSettingsStep
                            onComplete={handleSettingsComplete}
                            onBack={() => setCurrentStep(2)}
                            selectedProfession={selectedProfession}
                            selectedSummaryTypes={selectedSummaryTypes}
                            availableJobs={availableJobs}
                            availableSummaries={availableSummaries}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const StepContainer: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    return (
        <div className="w-[80vw] p-5 rounded-lg bg-white overflow-y-auto">
            {children}
        </div>
    );
};

const ProfessionStep: React.FC<{
    onNext: (professionId: number) => void;
    initialSelected: number | null;
    setAvailableJobs: React.Dispatch<React.SetStateAction<JobType[]>>;
    availableJobs: JobType[];
    profile: any;
}> = ({ onNext, initialSelected, setAvailableJobs, availableJobs, profile }) => {
    const { addNotification } = useNotifications();
    const [selectedProfession, setSelectedProfession] = useState<number | null>(initialSelected);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError,] = useState<string | null>(null);

    const selectProfession = (jobId: number) => {
        const job = availableJobs.find(j => j.id === jobId);
        const actualJobId = (job && job.job_name.toLowerCase() === 'onbekend') ? 0 : jobId;
        
        setSelectedProfession(actualJobId);
        onNext(actualJobId);
    };

    useEffect(() => {
        fetchJobTypes()
          .then((jobTypes) => setAvailableJobs(jobTypes))
          .catch((error) =>
            addNotification("error", error instanceof Error ? error.message : "Failed to fetch job types", 5000)
          );
    }, []);

    const sortedJobs = [...availableJobs].sort((a, b) => {
        if (a.id === 0) return 1;
        if (b.id === 0) return -1;
        return 0;
    });

    return (
        <StepContainer>
            <div className="flex flex-col h-full">
                <div className="max-w-2xl mx-auto text-center px-4">
                    <h2 className="text-3xl font-bold text-gray-800 mb-1">
                        Welkom, {profile?.naam || 'nieuwe gebruiker'}!
                    </h2>
                    <div className="space-y-4 mb-4">
                        <p className="text-gray-600 leading-relaxed">
                            We zijn blij je aan boord te hebben! Om je ervaring te optimaliseren,
                            kunnen we een aantal instellingen voor je personaliseren.
                        </p>
                    </div>

                    <p className="text-m mb-3">Kies uw beroep</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {sortedJobs.map((job) => (
                        <button
                            key={job.id}
                            className={`p-4 rounded-lg border-2 transition-colors  ${
                                selectedProfession === job.id
                                ? 'border-[#70c1a1] bg-green-50' 
                                : 'border-gray-200 hover:border-[#70c1a1]'
                            }`}
                            onClick={() => selectProfession(job.id)}
                        >
                            {job.id === 0 && job.job_name.toLowerCase() === 'onbekend' 
                                ? 'Overig' 
                                : job.job_name}
                        </button>
                    ))}
                </div>
            </div>
        </StepContainer>
    );
};

const SummaryTypeStep: React.FC<{
    onNext: (summaryType: number[]) => void;
    onBack: () => void;
    selectedProfession: number | null;
    initialSelected: number[];
    setAvailableSummaries: React.Dispatch<React.SetStateAction<SummaryTypeData[]>>;
}> = ({ onNext, onBack, selectedProfession, initialSelected, setAvailableSummaries }) => {
    const { addNotification } = useNotifications();
    const [selectedTypes, setSelectedTypes] = useState<number[]>(initialSelected);
    const [availableSummaries, setAvailableSummariesState] = useState<SummaryTypeData[]>([]);
    const [jobSummaries, setJobSummaries] = useState<JobSummary[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const toggleSummaryType = (summaryId: number) => {
        setSelectedTypes(prev => 
            prev.includes(summaryId)
                ? prev.filter(id => id !== summaryId)
                : [...prev, summaryId]
        );
    };

    const getSortedSummaries = () => {
        if (!availableSummaries || !jobSummaries || !selectedProfession) {
            return availableSummaries;
        }

        const relevantSummaryIds = jobSummaries
            .filter(js => js.job_id === selectedProfession)
            .map(js => js.summarie_id);

        return [...availableSummaries].sort((a, b) => {
            const aIsRelevant = relevantSummaryIds.includes(a.id);
            const bIsRelevant = relevantSummaryIds.includes(b.id);
            if (aIsRelevant && !bIsRelevant) return -1;
            if (!aIsRelevant && bIsRelevant) return 1;
            return 0;
        });
    };

    useEffect(() => {
        if (jobSummaries.length > 0 && availableSummaries.length > 0) {
            if (initialSelected.length === 0) {
                const relevantSummaryIds = jobSummaries
                    .filter(js => selectedProfession && js.job_id === selectedProfession)
                    .map(js => js.summarie_id);

                setSelectedTypes(relevantSummaryIds);
            }
        }
    }, [jobSummaries, availableSummaries, selectedProfession, initialSelected]);

    useEffect(() => {
        const token = getJwtToken();
        if (!token) return;

        const fetchSummaryTypes = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(
                    `${config.reactServer}feedback/all-summary-types`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(`Summary types fetch failed: ${response.status}`);
                }

                const summaryData = await response.json();
                
                if (!summaryData.summary_types) {
                    throw new Error('No summary_types found in response');
                }

                setAvailableSummaries(summaryData.summary_types);
                setAvailableSummariesState(summaryData.summary_types);
            } catch (error) {
                addNotification("error", error instanceof Error ? error.message : 'Failed to fetch summary types', 5000);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSummaryTypes();
    }, []);

    useEffect(() => {      
        fetchJobSummaries()
          .then((data) => setJobSummaries(data))
          .catch((error) => addNotification("error",error instanceof Error ? error.message : "Failed to fetch summary types",5000)
          )
    }, []);

    return (
        <StepContainer>
            <div className="flex flex-col h-full">
                <h2 className="text-2xl font-bold mb-3">Kies type samenvatting</h2>
                <div className="bg-green-50 border border-[#70c1a1] rounded-lg p-4 mb-6">
                    <p className="text-sm text-black">
                    Op basis van uw gekozen beroep hebben we relevante samenvattingen voorgeselecteerd. 
                    U kunt deze selectie naar wens aanpassen.
                    </p>
                </div>
                <div className="border-2 border-gray-200 rounded-lg h-[260px] mb-6">
                    <div className="h-full overflow-y-auto p-4">
                        {isLoading ? (
                            <div className="text-center py-8">
                                <p className="text-gray-600">Samenvattingstypes worden geladen...</p>
                            </div>
                        ) : error ? (
                            <div className="text-center py-8">
                                <p className="text-red-600">{error}</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {getSortedSummaries().map((summary) => (
                                    <button 
                                        key={summary.id}
                                        className={`p-4 rounded-lg border-2 text-left transition-colors ${
                                            selectedTypes.includes(summary.id)
                                            ? 'border-[#70c1a1] bg-green-50' 
                                            : 'border-gray-200 hover:border-[#70c1a1]'
                                        }`}
                                        onClick={() => toggleSummaryType(summary.id)}
                                    >
                                        {summary.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex justify-between gap-4 mt-auto">
                    <Button variant="backButton" onClick={onBack}>
                        Terug
                    </Button>
                    <Button
                        variant="nextButton" 
                        onClick={() => onNext(selectedTypes)}
                        disabled={selectedTypes.length === 0}
                    >
                        Volgende
                    </Button>
                </div>
            </div>
        </StepContainer>
    );
};

const OtherSettingsStep: React.FC<{
    onComplete: () => void;
    onBack: () => void;
    selectedProfession: number | null;
    selectedSummaryTypes: number[];
    availableJobs: JobType[];
    availableSummaries: SummaryTypeData[];
}> = ({ onComplete, onBack, selectedProfession, selectedSummaryTypes, availableJobs, availableSummaries }) => {
    const { addNotification } = useNotifications();
    const [profile, setProfile] = useState<any>(null);
    const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState<boolean>(false);
    const [showRecentConversation, setShowRecentConversation] = useState<boolean>(false);

    useEffect(() => {
        const fetchAndSetProfile = async () => {
            try {
                const data = await fetchProfile();
                setProfile(data);
                setIsTwoFactorEnabled(Boolean(data.twofactor)); 
            } catch (error) {
                console.error("Error fetching profile:", error);
            }
        };
    
        fetchAndSetProfile();
    }, []);
    
    const handleTwoFactorToggle = async () => {
        if (!profile) return;
    
        try {
            const newStatus = await toggleTwoFactorAuth(profile, isTwoFactorEnabled);
            setIsTwoFactorEnabled(newStatus); 
        } catch (error) {
            console.error("Error updating 2FA status:", error);
        }
    };

    const getSummaryName = (summaryId: number) => {
        const summary = availableSummaries.find(s => s.id === summaryId);
        return summary ? summary.name : 'Unknown';
    };

    const selectedJobName = availableJobs.find(job => selectedProfession === job.id)?.job_name;

    const handleRecentConversationToggle = async () => {
        try {
          const token = getJwtToken();
    
          if (!profile) {
            console.error("Profile not found. Cannot update settings.");
            return;
          }
    
          const response = await fetch(
            reactServer + "settings/recent-conversation",
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                email: profile.email,
                showRecentConversation: !showRecentConversation,
              }),
            }
          );
    
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
    
          setShowRecentConversation(!showRecentConversation);
        } catch (error) {
          console.error("Error updating recent conversation setting:", error);
        }
      };

    return (
        <StepContainer>
            <h2 className="text-2xl font-bold mb-6">Overzicht en beveiliging</h2>
            <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Gekozen beroep:</h3>
                    <div className="flex flex-wrap gap-2">
                        {selectedJobName && (
                            <span className="bg-[#70c1a1] text-white px-3 py-1 rounded-full text-sm">
                                {selectedJobName}
                            </span>
                        )}
                    </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Gekozen samenvattingen:</h3>
                    <div className="flex flex-wrap gap-2">
                        {selectedSummaryTypes.map(summaryId => (
                            <span key={summaryId} className="bg-[#70c1a1] text-white px-3 py-1 rounded-full text-sm">
                                {getSummaryName(summaryId)}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Twee-factor authenticatie</h3>
                    <div className="text-sm text-gray-600 mb-2">
                        {isTwoFactorEnabled 
                            ? "Twee-factor authenticatie (2FA) is ingeschakeld." 
                            : "Twee-factor authenticatie (2FA) is uitgeschakeld."}
                    </div>
                    <div
                        className={`toggle-switch ${isTwoFactorEnabled ? "switch-on" : "switch-off"}`}
                        onClick={handleTwoFactorToggle}
                    >
                        <div className="toggle-thumb"></div>
                    </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Recentste gesprek opslaan</h3>
                    <div className="text-sm text-gray-600 mb-2">
                        {showRecentConversation
                            ? "Gesprekken worden 24 uur versleuteld bewaard voor heropenen na bijvoorbeeld huisbezoeken." 
                            : "Recentste gesprek opslaan is uitgeschakeld. Dit betekent dat gesprekken niet worden bewaard voor heropenen."}
                    </div>
                    <div
                        className={`toggle-switch ${showRecentConversation ? "switch-on" : "switch-off"
                        }`}onClick={handleRecentConversationToggle}
                    >
                      <div className="toggle-thumb"></div>
                    </div>
                </div>
            </div>

            <div className="flex justify-between gap-4 mt-6">
                <Button variant="backButton" onClick={onBack}>
                    Terug
                </Button>
                <Button 
                    variant="nextButton"
                    onClick={() => {
                        onComplete();
                    }}
                >
                    Afronden
                </Button>
            </div>
        </StepContainer>
    );
};

export default PreferencesModal;